<script lang="ts">
  import { Chip, ChipDeprecated, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { type FacetValueLabelOptions, PairText, Text } from "@otto-ec/heureka-components/facet-value-label";
  import DeselectAll from "../facetvaluetags/DeselectAll.svelte";
  import type { DeselectAllConfiguration } from "./type";
  import { removeStatus } from "@otto-ec/heureka-components/tracking";
  import { isNba, isSelected } from "@otto-ec/heureka-components/facet";
  import { dereference, extendLabel, readLabel, updateLabel, updateStatus } from "../tracking/FeatureTracking";
  import { setChangeLabels } from "../filter/FilterTracking";
  import { heureka } from "@otto-ec/heureka";
  import { ocSheetContent, sheetContent } from "../filtersheet/Selectors";
  import { updateSanTopQuickFacet } from "./NextBestActionComponent";
  import { trackInAllEvents } from "../tracking/GlobalTracking";
  import tracker from "../tracking/Tracker";
  import { getElementById } from "../util/Utils";
  import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
  import { emptyCategoryPathStore, selectedCategoryPathValues } from "../categorypath/categoryPathStore";
  import type { TrackingLabels } from "../tracking/TrackingLabels";
  import { isActive, isNotActive } from "../toggle/Toggle";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    marginLeft: false,
    size: "75",
    showCount: false,
  };

  interface Props {
    nbaTitles?: string[];
    deselectAll: DeselectAllConfiguration;
    containsOnlyNbaValues?: boolean;
    values?: FacetValue[];
    hideDeselectAllOnDesktopView?: boolean;
    withExpander?: boolean;
    rule?: string;
  }

  const {
    nbaTitles,
    deselectAll,
    containsOnlyNbaValues,
    values = [],
    hideDeselectAllOnDesktopView,
    withExpander,
    rule,
  }: Props = $props();

  /*                                              */
  const uniqueNbaTitles = $derived(nbaTitles ? Array.from(new Set(nbaTitles)) : undefined);
  const topQuickFacet = $derived(uniqueNbaTitles?.join("|") || "none");
  const hasValues = $derived(values?.length > 0);

  /*                                                                             */
  const nbaValues: FacetValue[] = $derived(values?.map(removeStatus) ?? []);
  /*                                                       */
  const initialSelectedValues = $derived(nbaValues.filter((value) => value.checked) || []);
  const initialSelectedIds = $derived(initialSelectedValues.map((value) => value.referencedFacetValueId));
  const selectedCategories: FacetValue[] = $derived(
    Array.from($selectedCategoryPathValues)
      .filter((value) => !initialSelectedIds.includes(value.id))
      .map((value) => {
        return {
          id: value.id,
          checked: value.checked,
          title: value.title,
          referencedFacetValueId: value.ref || value.id,
        };
      }),
  );
  const selectedValues = $derived(initialSelectedValues.concat(selectedCategories));
  const selectedIds = $derived(selectedValues.map((value) => value.referencedFacetValueId));
  const unselectedValues = $derived(
    nbaValues
      .filter((value) => !selectedIds.includes(value.referencedFacetValueId))
      .map((value) => ({ ...value, checked: false })) ?? [],
  );
  const allValues = $derived(selectedValues.concat(unselectedValues));
  const hasSelectedValues = $derived(selectedValues.length > 0);

  const ottoSwitchValue: HTMLInputElement | undefined = getElementById<HTMLInputElement>(
    "facetValue_retailer_otto_otto",
  );
  function deleteOttoSwitch(filterMethod: TrackingLabels["san_FilterMethod"]) {
    if (ottoSwitchValue) {
      ottoSwitchValue.checked = false;
      updateStatus(ottoSwitchValue, "deleted", 2);
      updateLabel(ottoSwitchValue, "san_FilterMethod", filterMethod);
    }
  }

  function onchange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        if (event.target instanceof HTMLInputElement && event.target.checked === false) {
          if (event.target.value === ottoSwitchValue?.value) {
            deleteOttoSwitch("x_single_top");
          }
          filterForm.dataset.tsFeatureFilterMethod = "x_single_top";
        } else {
          filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
        }
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  function onClickDeselectAll() {
    deleteOttoSwitch("x_all_top");
    selectedValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FilterMethod", "x_all_top");
          updateLabel(referenced, "san_FacetLocation", "list_initial");
          updateStatus(referenced, "deleted", 2);
        }
      }
    });
  }

  function onSubmitDeselectAll(event: SubmitEvent) {
    emptyCategoryPathStore();
    selectedValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById<HTMLInputElement>(value.referencedFacetValueId);
        if (referenced) {
          clearFormInput(referenced);
        }
      }
      interceptSubmit(event);
    });
  }

  /*                                                                          */
  /*                                                                                        */
  let nbaElement: HTMLDivElement | undefined = $state();
  let isOcMounted = false;

  $effect(() => {
    if (isNotActive("HEUREKA_1975_OC_CHIP_MIGRATION")) return;
    if (!nbaElement) return;

    const handler = (event: Event) => {
      if (isOcMounted) return;
      /*                                           */
      if (!(event.target instanceof HTMLElement) || !event.target.tagName.toLowerCase().startsWith("oc-chip")) return;
      isOcMounted = true;
      heureka.nextBestAction_loaded.emit();
    };

    nbaElement.addEventListener("oc-mount", handler);
    return () => nbaElement?.removeEventListener("oc-mount", handler);
  });

  /*       */
  function trackTopQuickFacet() {
    /*                                                                                         */
    updateSanTopQuickFacet(sheetContent(), topQuickFacet);
    updateSanTopQuickFacet(ocSheetContent(), topQuickFacet);
    trackInAllEvents("san_TopQuickFacet", topQuickFacet);
  }

  $effect(() => {
    values.filter(isSelected).forEach((value, index) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FacetValueTagPosition", (index + 1).toString());
        }
      }
    });
    values.filter(isNba).forEach((value, index) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_NBAPosition", (index + 1).toString());
          extendLabel(referenced, "san_TopFacetValue", "nba");
        }
      }
    });
    trackTopQuickFacet();
    tracker.addToPageImpression({ san_NBAFacetValueCount: nbaValues.filter((value: FacetValue) => value.nba).length });
  });
</script>

{#snippet closeIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="close" size="50"></oc-icon-v1>
{/snippet}

<div
  bind:this={nbaElement}
  class="hc_nextBestAction{hasSelectedValues ? '' : ' hc_nextBestAction--unselected'}"
  hidden={!hasValues}
  data-rule={rule}
  data-ts-top-quick-facet={topQuickFacet}
  data-ts-nba-visible-values={topQuickFacet}
  data-ts-nba-facet-value-count={nbaValues.filter((value: FacetValue) => value.nba).length}
  data-ts-labels={JSON.stringify({
    san_NBAFacetValueCount: nbaValues.filter((value: FacetValue) => value.nba).length,
  })}
  {onchange}
>
  {#if hasValues}
    <h4>
      <span>
        {#if containsOnlyNbaValues && !(selectedValues.length > 0)}Beliebte Filter{:else}Gewählte und beliebte Filter{/if}
      </span>
      {#if hasSelectedValues}
        <span
          class="hc_nextBestAction__deselectAll"
          class:hc_facetValueTags__deselectAll--hide-desktop={hideDeselectAllOnDesktopView}
        >
          <DeselectAll
            {...deselectAll}
            fontSize="100"
            interactive={true}
            onclick={onClickDeselectAll}
            onsubmit={onSubmitDeselectAll}
          />
        </span>
      {/if}
    </h4>
    <div class="hc_nextBestAction__content">
      <div class="hc_nextBestAction__values">
        {#if values.length > 0}
          <div class="hc_facetValueGroup__container">
            <ul class="hc_facetValueGroup__list">
              {#each allValues as facetValue (facetValue.id + facetValue.title)}
                <li class="hc_facetValueGroup__item">
                  {#if isActive("HEUREKA_1975_OC_CHIP_MIGRATION")}
                    <Chip
                      class={[{ js_clearInput: facetValue.checked }]}
                      {...facetValue}
                      showRemoveIcon={!!facetValue.checked}
                    >
                      <!-- Overwrite retailer otto facet value title -->
                      {#if facetValue.title === "von OTTO"}
                        <PairText
                          prefix="Verkäufer: "
                          {...facetValue}
                          title="OTTO"
                          {...defaultFacetValueLabelOptions}
                        />
                      {:else}
                        <Text {...facetValue} {...defaultFacetValueLabelOptions} />
                      {/if}
                    </Chip>
                  {:else}
                    <ChipDeprecated class={[{ js_clearInput: facetValue.checked }]} {...facetValue}>
                      <!-- Overwrite retailer otto facet value title -->
                      {#if facetValue.title === "von OTTO"}
                        <PairText
                          prefix="Verkäufer: "
                          {...facetValue}
                          title="OTTO"
                          {...defaultFacetValueLabelOptions}
                        />
                      {:else}
                        <Text {...facetValue} {...defaultFacetValueLabelOptions} />
                      {/if}
                      {#if facetValue.checked}
                        {@render closeIcon()}
                      {/if}
                    </ChipDeprecated>
                  {/if}
                </li>
              {/each}
              {#if withExpander}
                <li class="hc_facetValueGroup--showMore" hidden>
                  <oc-link-v2 size="75">Mehr anzeigen</oc-link-v2>
                </li>
                <li class="hc_facetValueGroup--showLess" hidden>
                  <oc-link-v2 size="75">Weniger anzeigen</oc-link-v2>
                </li>
              {/if}
            </ul>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_nextBestAction {
    h4 {
      display: flex;
      padding-bottom: tokens.$oc-semantic-spacing-50;
      font: tokens.$oc-semantic-font-copy-100;
      color: tokens.$oc-semantic-color-text-secondary;
    }

    &__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px 0;
    }

    &__values {
      display: flex;
      /*              */
      gap: tokens.$oc-semantic-spacing-50;
      flex: 1 1 auto;
    }

    &__values {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;

      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }

      h4 {
        margin-bottom: var(--oc-semantic-spacing-75);
        font: var(--oc-semantic-font-copy-75);
        text-transform: uppercase;
        color: var(--oc-semantic-color-text-secondary);
      }

      ul {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--oc-semantic-spacing-50);

        /*                       */
        /*                                                                                                      */
        --overflow-box-bleed-block: calc(
          var(--oc-semantic-focus-outline-offset, 2px) + var(--oc-semantic-focus-outline-width, 2px)
        );
        overflow-x: auto;

        /*                                                                 */
        &::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        /*                      */
        scrollbar-width: none;
        margin-block: calc(-1 * var(--overflow-box-bleed-block));
        margin-inline: calc(-1 * var(--overflow-box-bleed-inline, 0));
        padding-block: var(--overflow-box-bleed-block);
        padding-inline: var(--overflow-box-bleed-inline, 0);
      }

      .hc_facetValueGroup__container {
        &:has(.hc_facetValueGroup--showMore) {
          width: 100%;
        }

        &--loading {
          pointer-events: none;
          opacity: 0.5;
        }
      }
    }

    &__deselectAll {
      text-align: left;
      padding-left: tokens.$oc-semantic-spacing-100;
      padding-right: tokens.$oc-semantic-spacing-25;
    }
  }
</style>
